import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  LOCALE_ID,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { ConfirmationDialog } from '../../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { ErrorVTO, ProductConfigVTO } from '../../../../../pages/admin-dashboard/products/product.model';
import { ProductService } from '../../../../../services/product/product.service';
import { Error } from '../../../../public-user-component/auth/shared/error/error';

type LimitField = 'orderLimitQuantity' | 'orderLimitDays' | 'orderLimitTotalCash';
type ErrorKey = 'format' | 'gt0' | 'whole' | 'max';

const isEmpty = (v: unknown) => v === null || v === undefined || String(v).trim() === '';

const numericFormat: ValidatorFn = (c) =>
  isEmpty(c.value) || /^-?\d+(\.\d+)?$/.test(String(c.value).trim()) ? null : { format: true };

const greaterThanZero: ValidatorFn = (c) => {
  if (isEmpty(c.value) || isNaN(Number(c.value))) return null;
  return Number(c.value) > 0 ? null : { gt0: true };
};

const wholeNumber: ValidatorFn = (c) => {
  if (isEmpty(c.value) || isNaN(Number(c.value))) return null;
  return Number.isInteger(Number(c.value)) ? null : { whole: true };
};

const toNumber = (v: string | null): number | null => (isEmpty(v) ? null : Number(v));

@Component({
  selector: 'app-product-config-form',
  standalone: true,
  imports: [ReactiveFormsModule, Error],
  templateUrl: './product-config-form.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductConfigForm implements OnInit, OnDestroy {
  productId = input.required<number>();
  config = input.required<ProductConfigVTO>();
  saved = output<ProductConfigVTO>();
  cancelled = output<void>();

  private fb = inject(FormBuilder);
  private productService = inject(ProductService);
  private locale = inject(LOCALE_ID);
  private sub = new Subscription();

  isArabic = computed(() => this.locale.startsWith('ar'));

  isSaving = signal(false);
  apiError = signal('');

  form = this.fb.group({
    orderLimitQuantity: ['', [numericFormat, greaterThanZero, wholeNumber, Validators.max(999999)]],
    orderLimitDays: ['', [numericFormat, greaterThanZero, wholeNumber, Validators.max(9999)]],
    orderLimitTotalCash: ['', [numericFormat, greaterThanZero, Validators.max(999999)]],
  });

  private errorOrder: ErrorKey[] = ['format', 'gt0', 'whole', 'max'];

  private maxMessage = {
    en: 'The entered value exceeds the maximum allowed value.',
    ar: 'القيمة المدخلة تتجاوز الحد الأقصى المسموح به.',
  };

  private formatMessage = {
    en: 'Value must be a valid number.',
    ar: 'يجب إدخال قيمة رقمية صحيحة.',
  };

  private messages: Record<LimitField, Partial<Record<ErrorKey, { en: string; ar: string }>>> = {
    orderLimitQuantity: {
      format: this.formatMessage,
      gt0: { en: 'Product order limit must be greater than 0.', ar: 'يجب أن يكون حد الطلب للصنف أكبر من صفر.' },
      whole: { en: 'Value must be a whole number.', ar: 'يجب أن تكون القيمة رقمًا صحيحًا.' },
      max: this.maxMessage,
    },
    orderLimitDays: {
      format: this.formatMessage,
      gt0: { en: 'Order limit period must be greater than 0.', ar: 'يجب أن تكون فترة الحد أكبر من صفر.' },
      whole: { en: 'Order limit period must be a whole number.', ar: 'يجب أن تكون فترة الحد رقماً صحيحاً.' },
      max: this.maxMessage,
    },
    orderLimitTotalCash: {
      format: { en: 'Invoice value must be a valid number.', ar: 'يجب إدخال قيمة رقمية صحيحة.' },
      gt0: { en: 'Invoice value must be greater than 0.', ar: 'يجب أن تكون قيمة الفاتورة أكبر من صفر.' },
      max: this.maxMessage,
    },
  };

  ngOnInit() {
    this.fill();
  }

  private asText(v: number | null | undefined): string {
    return v == null ? '' : String(v);
  }

  private fill() {
    const c = this.config();
    this.form.reset({
      orderLimitQuantity: this.asText(c.orderLimitQuantity),
      orderLimitDays: this.asText(c.orderLimitDays ?? 1),
      orderLimitTotalCash: this.asText(c.orderLimitTotalCash),
    });
    this.apiError.set('');
  }

  errorOf(name: LimitField): string | null {
    const c = this.form.controls[name];
    if (!c.touched || !c.errors) return null;
    const errors = c.errors;
    const key = this.errorOrder.find((k) => errors[k]);
    if (!key) return null;
    return this.messages[name][key]?.[this.isArabic() ? 'ar' : 'en'] ?? null;
  }

  inputClass(name: LimitField): string {
    const base = 'w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors placeholderColor';
    return this.errorOf(name) ? `${base} bordererrorcolor` : `${base} borderhelpercolortwo`;
  }

  daysWrapperClass(): string {
    const base = 'flex overflow-hidden rounded-lg border transition-colors';
    return this.errorOf('orderLimitDays') ? `${base} bordererrorcolor` : `${base} borderhelpercolortwo`;
  }

  cancel() {
    this.fill();
    this.cancelled.emit();
  }

  save() {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const v = this.form.getRawValue();
    const quantity = toNumber(v.orderLimitQuantity);
    const days = toNumber(v.orderLimitDays);
    const cash = toNumber(v.orderLimitTotalCash);

    const body: ProductConfigVTO = {
      isVATApplied: this.config().isVATApplied,
      orderLimitQuantity: quantity,
      orderLimitDays: quantity != null ? (days ?? 1) : null,
      orderLimitTotalCash: cash,
    };

    this.isSaving.set(true);
    this.apiError.set('');
    this.sub.add(
      this.productService
        .saveProductConfig(this.productId(), body)
        .pipe(finalize(() => this.isSaving.set(false)))
        .subscribe({
          next: () => this.saved.emit(body),
          error: (err: ErrorVTO) =>
            this.apiError.set(
              this.isArabic() ? err?.messageAr || 'حدث خطأ ما' : err?.messageEn || 'Some error occurred',
            ),
        }),
    );
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
